/**
 * race_lap.ts — the LOCAL lap state machine of the Grand Circuit (phase 5, pure):
 * sector splits, lap time, best lap, wrong-way warning, a 3-2-1 countdown when
 * the craft sits on the start line. No server state; the HUD renders it.
 */
export const HUD_RANGE_M = 400;
export const LINE_ZONE_M = 200;
export const COUNTDOWN_HOLD_S = 1.5;
export const COUNTDOWN_STOP_SPEED = 2;
export const COUNTDOWN_NEAR_M = 30;
export const WRONG_WAY_S = 1;

export interface LapState {
  onTrack: boolean;
  lastS: number | null;
  sector: number;
  lapStartT: number | null;
  sectorStartT: number | null;
  lastLap: number | null;
  best: number | null;
  splits: number[];
  wrongWayT: number;
  wrongWay: boolean;
  stillT: number;
  countdown: number | null;
  countdownT: number;
  armed: boolean;
}
export interface LapEvent { kind: 'sector' | 'lap' | 'wrongway' | 'countdown' | 'go' | 'enter' | 'leave'; value?: number }

export function initLap(best: number | null): LapState {
  return { onTrack: false, lastS: null, sector: 0, lapStartT: null, sectorStartT: null, lastLap: null, best, splits: [], wrongWayT: 0, wrongWay: false, stillT: 0, countdown: null, countdownT: 0, armed: false };
}

/** Signed loop delta in (−L/2, L/2]. */
export function loopDelta(from: number, to: number, length: number): number {
  let d = (to - from) % length;
  if (d > length / 2) d -= length;
  if (d <= -length / 2) d += length;
  return d;
}

/**
 * One step. `progress` is the projection onto the loop (arc length + distance
 * off it) or null; `sectorOf` maps an arc length to a sector index; `speed` m/s.
 */
export function stepLap(st: LapState, progress: { s: number; d: number } | null, length: number, sectorOf: (s: number) => number, speed: number, dt: number, now: number): LapEvent[] {
  const ev: LapEvent[] = [];
  const on = !!progress && progress.d <= HUD_RANGE_M;
  if (on !== st.onTrack) { st.onTrack = on; ev.push({ kind: on ? 'enter' : 'leave' }); if (!on) { st.lastS = null; st.wrongWay = false; st.wrongWayT = 0; st.countdown = null; } }
  if (!on || !progress) return ev;
  const s = progress.s;
  // Wrong way: a second of backward motion; cleared by forward motion.
  if (st.lastS !== null) {
    const delta = loopDelta(st.lastS, s, length);
    if (delta < -0.5) st.wrongWayT += dt; else if (delta > 0.5) st.wrongWayT = 0;
    const ww = st.wrongWayT >= WRONG_WAY_S;
    if (ww !== st.wrongWay) { st.wrongWay = ww; if (ww) ev.push({ kind: 'wrongway' }); }
    // The start line: a forward crossing from the last LINE_ZONE_M into the first.
    let crossed = false;
    if (delta > 0 && st.lastS > length - LINE_ZONE_M && s < LINE_ZONE_M) {
      crossed = true;
      // The last sector's split lands before the lap closes.
      if (st.sectorStartT !== null && st.lapStartT !== null) { const split = now - st.sectorStartT; st.splits[st.sector] = split; ev.push({ kind: 'sector', value: split }); }
      if (st.lapStartT !== null) {
        const lap = now - st.lapStartT;
        st.lastLap = lap;
        if (st.best === null || lap < st.best) st.best = lap;
        ev.push({ kind: 'lap', value: lap });
      }
      st.lapStartT = now; st.sectorStartT = now; st.splits = []; st.sector = 0; st.armed = false;
    }
    // Sector change (forward): the split of the sector just left.
    const sec = sectorOf(s);
    if (!crossed && sec !== st.sector && delta > 0) {
      if (st.sectorStartT !== null) { const split = now - st.sectorStartT; st.splits[st.sector] = split; ev.push({ kind: 'sector', value: split }); }
      st.sector = sec; st.sectorStartT = now;
    }
  } else st.sector = sectorOf(s);
  st.lastS = s;
  // Countdown: stationary on the line for COUNTDOWN_HOLD_S → 3, 2, 1, GO (the lap arms).
  const nearLine = s < COUNTDOWN_NEAR_M || s > length - COUNTDOWN_NEAR_M;
  if (nearLine && speed < COUNTDOWN_STOP_SPEED && st.countdown === null && !st.armed) {
    st.stillT += dt;
    if (st.stillT >= COUNTDOWN_HOLD_S) { st.countdown = 3; st.countdownT = 0; ev.push({ kind: 'countdown', value: 3 }); }
  } else if (st.countdown === null) st.stillT = 0;
  if (st.countdown !== null) {
    st.countdownT += dt;
    if (st.countdownT >= 1) {
      st.countdownT -= 1;
      st.countdown -= 1;
      if (st.countdown > 0) ev.push({ kind: 'countdown', value: st.countdown });
      else { st.countdown = null; st.armed = true; st.stillT = 0; st.lapStartT = now; st.sectorStartT = now; st.splits = []; ev.push({ kind: 'go' }); }
    }
  }
  return ev;
}

export function fmtLap(ms: number | null): string {
  if (ms === null) return '--:--.-';
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000), t = Math.floor((ms % 1000) / 100);
  return `${m}:${String(s).padStart(2, '0')}.${t}`;
}
